/* eslint-disable no-unused-vars */
import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Menu, LogOut, ArrowLeft, Shield, Eye, MessageSquare, Users, Brain, Database, Download, Trash2, Key, Smartphone, AlertTriangle, CheckCircle, Clock, Fingerprint, Settings, User } from 'lucide-react';
import { checkAuth, logout } from '../auth/config';
import NavigationCarousel from '../components/NavigationCarousel';
import { useTheme } from '../contexts/ThemeContext';
import DarkModeToggle from '../components/DarkModeToggle';

const PrivacySecurity = () => {
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  // Check for signup user first, then demo user as fallback
  const signupUser = JSON.parse(localStorage.getItem('user') || 'null');
  const demoUser = checkAuth();
  const user = signupUser || demoUser;
  
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(true);

  // Privacy Settings State
  const [profileVisibility, setProfileVisibility] = useState('connections');
  const [showActivityStatus, setShowActivityStatus] = useState(true);
  const [whoCanMessage, setWhoCanMessage] = useState('connections');
  const [whoCanViewProjects, setWhoCanViewProjects] = useState('institution');
  const [aiPersonalization, setAiPersonalization] = useState(true);
  const [dataSharing, setDataSharing] = useState(false);

  // Security Settings State
  const [twoFactorAuth, setTwoFactorAuth] = useState(false);
  const [loginAlerts, setLoginAlerts] = useState(true);
  const [biometricLogin, setBiometricLogin] = useState(false);
  const [autoLogout, setAutoLogout] = useState('15');

  const toggleSidebar = () => setIsSidebarCollapsed(prev => !prev);

  const sidebarMenuItems = [
    { label: 'HOME', path: '/app' },
    { label: 'STUDY', path: '/app/study' },
    { label: 'CHALLENGES', path: '/app/challenges' },
    { label: 'COMMUNITY', path: '/app/community' },
    { label: 'PIN UP', path: '/app/pinup' },
    { label: 'PROGRESS', path: '/app/progress' },
    { label: 'PROFILE', path: '/app/profile' },
    { label: 'LOG OUT', path: '/login', isLogout: true }
  ];

  const handleLogout = () => {
    logout();
    localStorage.removeItem('user');
    localStorage.removeItem('isLoggedIn');
    localStorage.removeItem('onboardingComplete');
    window.location.href = '/login';
  };

  const handleBack = () => {
    navigate('/app/profile');
  };

  // Custom Toggle Component for 2 options
  const ToggleSwitch = ({ isOn, onToggle, leftLabel, rightLabel }) => (
    <div className="flex items-center gap-3">
      <span className={`text-sm font-medium ${!isOn ? (isDarkMode ? 'text-gray-300' : 'text-gray-700') : (isDarkMode ? 'text-gray-500' : 'text-gray-400')}`}>
        {leftLabel}
      </span>
      <button
        onClick={onToggle}
        className={`relative w-12 h-6 rounded-full transition-colors ${
          isOn ? (isDarkMode ? 'bg-gray-600' : 'bg-gray-200') : (isDarkMode ? 'bg-gray-700' : 'bg-gray-400')
        }`}
      >
        <div
          className={`absolute top-1 w-4 h-4 ${isDarkMode ? 'bg-gray-300' : 'bg-gray-600'} rounded-full transition-transform ${
            isOn ? 'translate-x-7' : 'translate-x-1'
          }`}
        />
      </button>
      <span className={`text-sm font-medium ${isOn ? (isDarkMode ? 'text-gray-300' : 'text-gray-700') : (isDarkMode ? 'text-gray-500' : 'text-gray-400')}`}>
        {rightLabel}
      </span>
    </div>
  );

  // Custom Toggle Component for 3 options
  const ThreeOptionToggle = ({ value, onChange, options }) => (
    <div className={`flex items-center gap-2 rounded-lg p-2 ${isDarkMode ? 'bg-gray-700' : ''}`} style={{ backgroundColor: isDarkMode ? undefined : '#DDDDDD' }}>
      {options.map((option) => (
        <button
          key={option.value}
          onClick={() => onChange(option.value)}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            value === option.value
              ? (isDarkMode ? 'bg-white text-black' : 'bg-white text-black')
              : (isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-[#DDDDDD] text-gray-600')
          }`}
        >
          {option.label}
        </button>
      ))}
    </div>
  );

  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-gray-900' : 'bg-gray-100'}`}>
      {/* Sidebar Navigation */}
      <div className="fixed top-0 left-0 h-screen z-40">
        <NavigationCarousel
          menuItems={sidebarMenuItems}
          onItemClick={(item)=> item.isLogout ? handleLogout() : navigate(item.path)}
          isCollapsed={isSidebarCollapsed}
          onToggleSidebar={toggleSidebar}
        />
      </div>

      {/* Main Content - Adjusted for Sidebar */}
      <div className={`transition-all duration-300 ${isSidebarCollapsed ? 'ml-20' : 'ml-56'}`}>
        {/* Header */}
        <div className="bg-[#AC5757] rounded-l-lg" style={{ 
          marginLeft: '10px',
          transition: 'margin-left 0.3s ease'
        }}>
          <div className="flex items-center justify-between px-6 py-4">
            <div className="flex items-center gap-3">
              <button 
                onClick={handleBack}
                className={`p-2 rounded-lg transition-colors ${isDarkMode ? 'hover:bg-red-700' : 'hover:bg-red-600'}`}
              >
                <ArrowLeft size={20} className="text-white" />
              </button>
              <h1 className="font-oswald font-medium text-white text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-[65px]">PRIVACY & SECURITY</h1>
            </div>
            <div className="flex items-center gap-3">
              <DarkModeToggle size="small" />
              <Link to="/app/messages" className={`p-2 rounded-lg transition-colors ${isDarkMode ? 'hover:bg-red-700' : 'hover:bg-red-600'}`}>
                <MessageSquare size={20} className="text-white" />
              </Link>
              <Link to="/app/profile" className={`p-2 rounded-lg transition-colors ${isDarkMode ? 'hover:bg-red-700' : 'hover:bg-red-600'}`}>
                <User size={20} className="text-white" />
              </Link>
              <button onClick={handleLogout} className={`p-2 rounded-lg transition-colors ${isDarkMode ? 'hover:bg-red-700' : 'hover:bg-red-600'}`}>
                <LogOut size={20} className="text-white" />
              </button>
            </div>
          </div>
        </div>

        <div className="max-w-4xl px-6 py-8" style={{ marginLeft: '20px', transition: 'margin-left 0.3s ease' }}>
          {/* Page Title */}
          <div className="mb-8">
            <h2 className={`${isDarkMode ? 'text-white' : 'text-gray-900'} text-3xl font-bold mb-2`}>Privacy & Security</h2>
            <p className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'} text-lg`}>Manage your privacy settings and account security.</p>
          </div>

          {/* Privacy Section */}
          <section className="mb-12">
            <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-2xl shadow-lg overflow-hidden`}>
              <div className="bg-[#AC5757] px-6 py-4">
                <div className="flex items-center gap-3">
                  <Eye size={24} className="text-white" />
                  <h3 className="text-xl font-bold text-white">Privacy</h3>
                </div>
              </div>
              
              <div className="p-6 space-y-8">
                {/* Profile & Visibility */}
                <div>
                  <h4 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Profile & Visibility</h4>
                  
                  {/* Profile Visibility Toggle */}
                  <div className={`${isDarkMode ? 'border-gray-700' : 'border-gray-200'} flex items-center justify-between py-4 border-b`}>
                    <div>
                      <div className={`${isDarkMode ? 'text-gray-100' : 'text-gray-900'} font-medium`}>Profile Visibility</div>
                      <div className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'} text-sm`}>Control who can see your profile and work</div>
                    </div>
                    <ThreeOptionToggle
                      value={profileVisibility}
                      onChange={setProfileVisibility}
                      options={[
                        { value: 'public', label: 'Public' },
                        { value: 'connections', label: 'Connections Only' },
                        { value: 'private', label: 'Private' }
                      ]}
                    />
                  </div>

                  {/* Show Activity Status */}
                  <div className={`${isDarkMode ? 'border-gray-700' : 'border-gray-200'} flex items-center justify-between py-4 border-b`}>
                    <div>
                      <div className={`${isDarkMode ? 'text-gray-100' : 'text-gray-900'} font-medium`}>Show Activity Status</div>
                      <div className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'} text-sm`}>Let others see when you're online</div>
                    </div>
                    <ToggleSwitch
                      isOn={showActivityStatus}
                      onToggle={() => setShowActivityStatus(!showActivityStatus)}
                      leftLabel="Hidden"
                      rightLabel="Visible"
                    />
                  </div>

                  {/* Who Can Message Me */}
                  <div className={`${isDarkMode ? 'border-gray-700' : 'border-gray-200'} flex items-center justify-between py-4 border-b`}>
                    <div>
                      <div className={`${isDarkMode ? 'text-gray-100' : 'text-gray-900'} font-medium`}>Who Can Message Me</div>
                      <div className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'} text-sm`}>Control who can send you direct messages</div>
                    </div>
                    <ToggleSwitch
                      isOn={whoCanMessage === 'everyone'}
                      onToggle={() => setWhoCanMessage(whoCanMessage === 'everyone' ? 'connections' : 'everyone')}
                      leftLabel="Connections Only"
                      rightLabel="Everyone"
                    />
                  </div>

                  {/* Who Can View My Projects */}
                  <div className="flex items-center justify-between py-4">
                    <div>
                      <div className={`${isDarkMode ? 'text-gray-100' : 'text-gray-900'} font-medium`}>Who Can View My Projects</div>
                      <div className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'} text-sm`}>Control visibility of your project portfolio</div>
                    </div>
                    <ThreeOptionToggle
                      value={whoCanViewProjects}
                      onChange={setWhoCanViewProjects}
                      options={[
                        { value: 'everyone', label: 'Everyone' },
                        { value: 'institution', label: 'My Institution' },
                        { value: 'onlyme', label: 'Only Me' }
                      ]}
                    />
                  </div>
                </div>

                {/* Data & Personalization */}
                <div>
                  <h4 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Data & Personalization</h4>
                  
                  {/* AI Personalization */}
                  <div className={`${isDarkMode ? 'border-gray-700' : 'border-gray-200'} flex items-center justify-between py-4 border-b`}>
                    <div>
                      <div className={`${isDarkMode ? 'text-gray-100' : 'text-gray-900'} font-medium`}>AI Personalization</div>
                      <div className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'} text-sm`}>Use AI to personalize your learning experience</div>
                    </div>
                    <ToggleSwitch
                      isOn={aiPersonalization}
                      onToggle={() => setAiPersonalization(!aiPersonalization)}
                      leftLabel="Off"
                      rightLabel="On"
                    />
                  </div>

                  {/* Data Sharing for Research */}
                  <div className="flex items-center justify-between py-4">
                    <div>
                      <div className={`${isDarkMode ? 'text-gray-100' : 'text-gray-900'} font-medium`}>Data Sharing for Research</div>
                      <div className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'} text-sm`}>Help improve the platform through anonymous data sharing</div>
                    </div>
                    <ToggleSwitch
                      isOn={dataSharing}
                      onToggle={() => setDataSharing(!dataSharing)}
                      leftLabel="Off"
                      rightLabel="On"
                    />
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Security Section */}
          <section className="mb-12">
            <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-2xl shadow-lg overflow-hidden`}>
              <div className="bg-[#AC5757] px-6 py-4">
                <div className="flex items-center gap-3">
                  <Shield size={24} className="text-white" />
                  <h3 className="text-xl font-bold text-white">Security</h3>
                </div>
              </div>
              
              <div className="p-6 space-y-8">
                {/* Login Security */}
                <div>
                  <h4 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Login Security</h4>
                  
                  <div className="space-y-4">
                    <button className={`${isDarkMode ? 'border-gray-700 hover:bg-gray-700/50' : 'border-gray-200 hover:bg-gray-50'} w-full flex items-center justify-between p-4 border rounded-lg transition-colors`}>
                      <div className="flex items-center gap-3">
                        <Key size={20} className="text-[#AC5757]" />
                        <div className="text-left">
                          <div className={`${isDarkMode ? 'text-gray-100' : 'text-gray-900'} font-medium`}>Change Password</div>
                          <div className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'} text-sm`}>Update your account password</div>
                        </div>
                      </div>
                      <div className="text-[#AC5757]">→</div>
                    </button>

                    <div className={`${isDarkMode ? 'border-gray-700' : 'border-gray-200'} flex items-center justify-between p-4 border rounded-lg`}>
                      <div className="flex items-center gap-3">
                        <Smartphone size={20} className="text-[#AC5757]" />
                        <div>
                          <div className={`${isDarkMode ? 'text-gray-100' : 'text-gray-900'} font-medium`}>Two-Factor Authentication</div>
                          <div className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'} text-sm`}>Add an extra layer of security</div>
                        </div>
                      </div>
                      <ToggleSwitch
                        isOn={twoFactorAuth}
                        onToggle={() => setTwoFactorAuth(!twoFactorAuth)}
                        leftLabel="Off"
                        rightLabel="On"
                      />
                    </div>

                    <button className={`${isDarkMode ? 'border-gray-700 hover:bg-gray-700/50' : 'border-gray-200 hover:bg-gray-50'} w-full flex items-center justify-between p-4 border rounded-lg transition-colors`}>
                      <div className="flex items-center gap-3">
                        <Settings size={20} className="text-[#AC5757]" />
                        <div className="text-left">
                          <div className={`${isDarkMode ? 'text-gray-100' : 'text-gray-900'} font-medium`}>Active Sessions</div>
                          <div className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'} text-sm`}>View and manage logged-in devices</div>
                        </div>
                      </div>
                      <div className="text-[#AC5757]">→</div>
                    </button>

                    <div className={`${isDarkMode ? 'border-gray-700' : 'border-gray-200'} flex items-center justify-between p-4 border rounded-lg`}>
                      <div className="flex items-center gap-3">
                        <AlertTriangle size={20} className="text-[#AC5757]" />
                        <div>
                          <div className={`${isDarkMode ? 'text-gray-100' : 'text-gray-900'} font-medium`}>Login Alerts</div>
                          <div className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'} text-sm`}>Get notified of suspicious activity</div>
                        </div>
                      </div>
                      <ToggleSwitch
                        isOn={loginAlerts}
                        onToggle={() => setLoginAlerts(!loginAlerts)}
                        leftLabel="Off"
                        rightLabel="On"
                      />
                    </div>
                  </div>
                </div>

                {/* Account Verification */}
                <div>
                  <h4 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Account Verification</h4>
                  
                  <div className="space-y-4">
                    <div className={`${isDarkMode ? 'border-gray-700' : 'border-gray-200'} flex items-center justify-between p-4 border rounded-lg`}>
                      <div className="flex items-center gap-3">
                        <CheckCircle size={20} className="text-green-600" />
                        <div>
                          <div className={`${isDarkMode ? 'text-gray-100' : 'text-gray-900'} font-medium`}>Email Verified</div>
                          <div className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'} text-sm`}>Your email address is verified</div>
                        </div>
                      </div>
                      <span className="text-green-600 text-sm font-medium">Verified</span>
                    </div>

                    <button className={`${isDarkMode ? 'border-gray-700 hover:bg-gray-700/50' : 'border-gray-200 hover:bg-gray-50'} w-full flex items-center justify-between p-4 border rounded-lg transition-colors`}>
                      <div className="flex items-center gap-3">
                        <Smartphone size={20} className="text-[#AC5757]" />
                        <div className="text-left">
                          <div className={`${isDarkMode ? 'text-gray-100' : 'text-gray-900'} font-medium`}>Verify Phone Number</div>
                          <div className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'} text-sm`}>Add phone number for account recovery</div>
                        </div>
                      </div>
                      <div className="text-[#AC5757]">→</div>
                    </button>
                  </div>
                </div>

                {/* Device & App Permissions */}
                <div>
                  <h4 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Device & App Permissions</h4>
                  
                  <div className="space-y-4">
                    <button className={`${isDarkMode ? 'border-gray-700 hover:bg-gray-700/50' : 'border-gray-200 hover:bg-gray-50'} w-full flex items-center justify-between p-4 border rounded-lg transition-colors`}>
                      <div className="flex items-center gap-3">
                        <Settings size={20} className="text-[#AC5757]" />
                        <div className="text-left">
                          <div className={`${isDarkMode ? 'text-gray-100' : 'text-gray-900'} font-medium`}>Manage Connected Devices</div>
                          <div className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'} text-sm`}>View and revoke device access</div>
                        </div>
                      </div>
                      <div className="text-[#AC5757]">→</div>
                    </button>

                    <button className={`${isDarkMode ? 'border-gray-700 hover:bg-gray-700/50' : 'border-gray-200 hover:bg-gray-50'} w-full flex items-center justify-between p-4 border rounded-lg transition-colors`}>
                      <div className="flex items-center gap-3">
                        <Shield size={20} className="text-[#AC5757]" />
                        <div className="text-left">
                          <div className={`${isDarkMode ? 'text-gray-100' : 'text-gray-900'} font-medium`}>Revoke Third-Party Access</div>
                          <div className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'} text-sm`}>Manage app integrations and permissions</div>
                        </div>
                      </div>
                      <div className="text-[#AC5757]">→</div>
                    </button>
                  </div>
                </div>

                {/* Advanced Protection */}
                <div>
                  <h4 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Advanced Protection</h4>
                  
                  <div className="space-y-4">
                    <div className={`${isDarkMode ? 'border-gray-700' : 'border-gray-200'} flex items-center justify-between p-4 border rounded-lg`}>
                      <div className="flex items-center gap-3">
                        <Fingerprint size={20} className="text-[#AC5757]" />
                        <div>
                          <div className={`${isDarkMode ? 'text-gray-100' : 'text-gray-900'} font-medium`}>Biometric Login</div>
                          <div className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'} text-sm`}>Use Face ID or fingerprint for quick access</div>
                        </div>
                      </div>
                      <ToggleSwitch
                        isOn={biometricLogin}
                        onToggle={() => setBiometricLogin(!biometricLogin)}
                        leftLabel="Off"
                        rightLabel="On"
                      />
                    </div>

                    <div className={`${isDarkMode ? 'border-gray-700' : 'border-gray-200'} flex items-center justify-between p-4 border rounded-lg`}>
                      <div className="flex items-center gap-3">
                        <Clock size={20} className="text-[#AC5757]" />
                        <div>
                          <div className={`${isDarkMode ? 'text-gray-100' : 'text-gray-900'} font-medium`}>Auto-Logout Timer</div>
                          <div className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'} text-sm`}>Automatically log out after inactivity</div>
                        </div>
                      </div>
                      <ThreeOptionToggle
                        value={autoLogout}
                        onChange={setAutoLogout}
                        options={[
                          { value: '10', label: '10 min' },
                          { value: '15', label: '15 min' },
                          { value: '30', label: '30 min' }
                        ]}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default PrivacySecurity;
